import { ReactNode, useMemo } from 'react';
import {
  MappedTemplate,
  MsgTemplate,
  Template,
  Parameters,
  ContactItem,
  LocationItem,
} from '../schema/message/inferface';
import {
  BannerTypeKeys,
  FileParams,
  GenerateLinkParams,
} from '../types/interface';
import { startCase } from 'lodash';
import { replacePlaceholders } from '../functions/replace-placeholders';
import { MessageType } from '../schema/message/enum';

type TemplateComponentsType =
  | 'video'
  | 'image'
  | 'header'
  | 'body'
  | 'document'
  | 'banner'
  | 'footer'
  | 'button'
  | 'location'
  | 'audio'
  | 'contact';

export type TemplateComponentsProps = Record<
  TemplateComponentsType,
  (templateName: string, text: string, extra?: FileParams[]) => ReactNode
>;

const mapComponentsToParams = (template: Template): MappedTemplate => {
  const { components = [], params = [] } = template;
  return components.map(({ type, text, format }) => {
    const { parameters = [] } =
      params.find((x) => x.type.toLowerCase() === type.toLowerCase()) || {};
    return { type, text, format, parameters };
  });
};

const extractParameters = (
  parameters: Parameters[],
  messageId: string,
  generateFileUrlPayload?: (
    mimeType: 'image' | 'document'
  ) => GenerateLinkParams
) => {
  const textParameters: string[] = [];
  const fileGroups: Record<
    'image' | 'document' | 'video' | 'audio',
    FileParams[]
  > = {
    image: [],
    document: [],
    video: [],
    audio: [],
  };

  parameters.forEach((param) => {
    const { text } = param;

    if (text?.trim()) textParameters.push(text);

    (['image', 'document', 'video', 'audio'] as const).forEach((key) => {
      const file = param[key];
      if (file) {
        fileGroups[key].push({
          ...file,
          messageId,
          generateLinkParams: generateFileUrlPayload?.(
            key === 'image' ? 'image' : 'document'
          ),
        });
      }
    });
  });

  return {
    textParameters,
    imageParameters: fileGroups.image,
    videoParameters: fileGroups.video,
    documentParameters: fileGroups.document,
    audioParameters: fileGroups.audio,
  };
};

interface RenderTemplateUiProps {
  messageId: string;
  template: MsgTemplate;
  bannerData?: { type: BannerTypeKeys; timetoken: string } | null;
  templateUI: TemplateComponentsProps;
  msgType?: MessageType;
  generateFileUrlPayload?: (
    mimeType: 'image' | 'document'
  ) => GenerateLinkParams;
  locationData?: LocationItem;
  contactData?: ContactItem | ContactItem[];
}

export const useRenderTemplateUi = ({
  messageId,
  template,
  bannerData = null,
  templateUI,
  msgType,
  generateFileUrlPayload,
  locationData,
  contactData,
}: RenderTemplateUiProps): ReactNode[] => {
  return useMemo(() => {
    // 📍 Location messages
    if (msgType === MessageType.LOCATION && locationData) {
      const el = templateUI.location(
        '',
        `${locationData.latitude}, ${locationData.longitude}`,
        locationData as any
      );
      return el ? [el] : [];
    }

    // 👥 Contact messages
    if (msgType === MessageType.CONTACT || msgType === MessageType.CONTACTS) {
      const el = templateUI.contact('', '', contactData as any);
      return el ? [el] : [];
    }

    // 🧩 Template-based messages
    const [templateKey, currentTemplate] = Object.entries(template)?.[0] ?? [];
    const templateName = (templateKey ?? '')
      .split('_')
      .map(startCase)
      .join(' ');
    const components = mapComponentsToParams(currentTemplate);
    const ui: ReactNode[] = [];

    for (const { type, text = '', parameters } of components) {
      const {
        documentParameters,
        imageParameters,
        textParameters,
        videoParameters,
        audioParameters,
      } = extractParameters(parameters, messageId, generateFileUrlPayload);

      let templateType: TemplateComponentsType = 'body';

      const isDocMessage =
        msgType &&
        [MessageType.INVOICE, MessageType.DISPATCH].includes(msgType);
      switch (type) {
        case 'HEADER':
          if (
            isDocMessage &&
            (imageParameters.length || documentParameters.length)
          ) {
            templateType = 'document';
          } else if (imageParameters.length) templateType = 'image';
          else if (videoParameters.length) templateType = 'video';
          else if (audioParameters.length) templateType = 'audio';
          else if (documentParameters.length) templateType = 'document';
          else templateType = 'header';
          break;
        case 'FOOTER':
          templateType = 'footer';
          break;
        case 'BUTTONS':
          templateType = 'button';
          break;
      }

      const result = replacePlaceholders(text, textParameters);
      const element = templateUI[templateType](templateName, result, [
        ...videoParameters,
        ...imageParameters,
        ...documentParameters,
        ...audioParameters,
      ]);

      if (bannerData && templateType === 'body') {
        ui.push(templateUI.banner(bannerData.type, bannerData.timetoken));
      }

      ui.push(element);
    }

    return ui;
  }, [
    messageId,
    bannerData,
    template,
    templateUI,
    msgType,
    locationData,
    contactData,
    generateFileUrlPayload,
  ]);
};
